import {
  gspx,
  GPX_EVENT_TYPES,
  GPX_FEATURES,
  GPX_CAPABILITIES,
  GPX_PARTNERS,
  GPX_ROUTES,
} from '@rsos/gainsight';

// Event names
const MEDIA_EVENT_NAMES = {
  REQUEST_VIDEO_STREAM: 'request_video_stream',
  REQUEST_MEDIA_UPLOAD: 'request_media_upload',
  REQUEST_DISPLAY_MEDIA: 'request_display_media',
  MEDIA_REQUEST_RESPONSE: 'media_request_response',
  PLAY_VIDEO: 'play_video',
  BLUR_VIDEO: 'blur_video',
  UNBLUR_VIDEO: 'unblur_video',
  BLUR_MEDIA: 'blur_media',
  UNBLUR_MEDIA: 'unblur_media',
  MEDIA_LOAD: 'media_load',
};

/**
 * Utility function to sanitize URLs by masking sensitive parameters.
 * @param {string} url - The URL to sanitize
 * @returns {string} The sanitized URL with sensitive parameters masked
 */
const sanitizeUrl = url => {
  if (!url) return url;
  try {
    const urlObj = new URL(url);
    if (urlObj.searchParams.has('access_token')) {
      urlObj.searchParams.set('access_token', '[REDACTED]');
    }
    return urlObj.toString();
  } catch (e) {
    return url;
  }
};

/**
 * Tracks when a user clicks to request a live video stream
 */
export const trackMediaLiveVideoClick = () => {
  gspx.trackEvent({
    name: MEDIA_EVENT_NAMES.REQUEST_VIDEO_STREAM,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.CIRCUS_LIVE_VIDEO,
    capability: GPX_CAPABILITIES.VIDEO_STREAMING,
    route: GPX_ROUTES.MEDIA,
    partner: GPX_PARTNERS.CIRCUS,
  });
};

/**
 * Tracks the response of a media request
 * @param {Object} params - The parameters for tracking the media request
 * @param {boolean} [params.isVideoRequest=false] - Whether this is a video request
 * @param {boolean} [params.responseSuccess=false] - Whether the request was successful
 * @param {string} params.videoUrl - The URL of the video stream
 * @param {string} params.mediaUrl - The URL of the media
 */
export const trackMediaRequest = ({
  isVideoRequest = false,
  responseSuccess = false,
  videoUrl,
  mediaUrl,
}) => {
  gspx.trackEvent({
    name: MEDIA_EVENT_NAMES.MEDIA_REQUEST_RESPONSE,
    type: GPX_EVENT_TYPES.API_RESPONSE,
    feature: isVideoRequest
      ? GPX_FEATURES.CIRCUS_LIVE_VIDEO
      : GPX_FEATURES.CIRCUS_MEDIA_UPLOAD,
    capability: isVideoRequest
      ? GPX_CAPABILITIES.VIDEO_STREAMING
      : GPX_CAPABILITIES.MEDIA_UPLOAD,
    route: GPX_ROUTES.MEDIA,
    partner: GPX_PARTNERS.CIRCUS,
    entities: {
      media_url: sanitizeUrl(mediaUrl),
      live_video_url: sanitizeUrl(videoUrl),
    },
    additionalKeys: {
      success: responseSuccess,
    },
  });
};

/**
 * Tracks when a user clicks to request a media upload
 */
export const trackMediaRequestMediaClick = () => {
  gspx.trackEvent({
    name: MEDIA_EVENT_NAMES.REQUEST_MEDIA_UPLOAD,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.CIRCUS_MEDIA_UPLOAD,
    capability: GPX_CAPABILITIES.MEDIA_UPLOAD,
    route: GPX_ROUTES.MEDIA,
    partner: GPX_PARTNERS.CIRCUS,
  });
};

/**
 * Tracks when a user clicks to display media
 */
export const trackDisplayMediaClick = () => {
  gspx.trackEvent({
    name: MEDIA_EVENT_NAMES.REQUEST_DISPLAY_MEDIA,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.CIRCUS_DISPLAY_MEDIA,
    capability: GPX_CAPABILITIES.MEDIA_UPLOAD,
    route: GPX_ROUTES.MEDIA,
    partner: GPX_PARTNERS.CIRCUS,
  });
};

/**
 * Tracks when a user plays a video
 * @param {string} mediaUrl - The URL of the video being played
 */
export const trackPlayVideo = mediaUrl => {
  gspx.trackEvent({
    name: MEDIA_EVENT_NAMES.PLAY_VIDEO,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.CIRCUS_DISPLAY_MEDIA,
    capability: GPX_CAPABILITIES.MEDIA_UPLOAD,
    route: GPX_ROUTES.MEDIA,
    partner: GPX_PARTNERS.CIRCUS,
    entities: { media_url: sanitizeUrl(mediaUrl) },
  });
};

/**
 * Tracks when a user clicks to blur or unblur a live video
 * @param {boolean} action - True for blur, false for unblur
 * @param {string} videoUrl - The URL of the video being blurred/unblurred
 */
export const trackMediaLiveVideoBlurUnblurClick = (action, videoUrl) => {
  gspx.trackEvent({
    name: action
      ? MEDIA_EVENT_NAMES.BLUR_VIDEO
      : MEDIA_EVENT_NAMES.UNBLUR_VIDEO,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.CIRCUS_LIVE_VIDEO,
    capability: GPX_CAPABILITIES.VIDEO_STREAMING,
    route: GPX_ROUTES.MEDIA,
    partner: GPX_PARTNERS.CIRCUS,
    entities: { live_video_url: sanitizeUrl(videoUrl) },
  });
};

/**
 * Tracks when a user clicks to blur or unblur media
 * @param {boolean} action - True for blur, false for unblur
 */
export const trackMediaBlurUnblurClick = action => {
  gspx.trackEvent({
    name: action
      ? MEDIA_EVENT_NAMES.BLUR_MEDIA
      : MEDIA_EVENT_NAMES.UNBLUR_MEDIA,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.CIRCUS_DISPLAY_MEDIA,
    capability: GPX_CAPABILITIES.MEDIA_UPLOAD,
    route: GPX_ROUTES.MEDIA,
    partner: GPX_PARTNERS.CIRCUS,
  });
};

/**
 * Tracks a response when obtaining downloadable media URL from BE
 * @param {Object} params - The parameters for tracking the media source request
 * @param {string} params.mediaUrl - The URL of the media
 * @param {string} params.contentType - The content type of the media
 * @param {string} params.requestUrl - The URL of the request
 * @param {boolean} [params.responseSuccess=false] - Whether the request was successful
 * @param {boolean} [params.isPreview=false] - Whether this is a preview request
 */
export const trackMediaSourceRequest = ({
  mediaUrl,
  contentType,
  requestUrl,
  responseSuccess = false,
  isPreview = false,
}) => {
  gspx.trackEvent({
    name: MEDIA_EVENT_NAMES.MEDIA_LOAD,
    type: GPX_EVENT_TYPES.API_RESPONSE,
    feature: GPX_FEATURES.CIRCUS_DISPLAY_MEDIA,
    capability: GPX_CAPABILITIES.MEDIA_UPLOAD,
    route: GPX_ROUTES.MEDIA,
    partner: GPX_PARTNERS.CIRCUS,
    entities: {
      media_url: sanitizeUrl(mediaUrl),
      content_type: contentType,
    },
    additionalKeys: {
      success: responseSuccess,
      is_preview: isPreview,
      request_url: sanitizeUrl(requestUrl),
    },
  });
};
